import type { FC } from "react";
import { useMemo } from "react";

import { orderBy } from "lodash";

import { colorsPalettes } from "@similarweb/styles";
import { MetricData } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";
import { getMarketDistributionBarChartConfig } from "app/pages/industry-analysis/overview/components/MarketDistribution/MarketDistributionBarChartConfig";
import {
    Banner,
    BannerText,
    BodyContainer,
    LeftContainer,
    LightBulbStyled,
    LeftMetricContainer,
    RightContainer,
} from "app/pages/industry-analysis/overview/components/MarketDistribution/MarketDistributionStyles";
import type { IMarketDistributionBarChart } from "app/pages/industry-analysis/overview/components/MarketDistribution/types";
import Chart from "pro-features/components/Chart/src/Chart";
import I18n from "pro-features/components/WithTranslation/src/I18n";

const getPercentageSignFilter = percentageSignFilter();
const i18n = i18nFilter();

export const MarketDistributionBody: FC<IMarketDistributionBarChart> = ({ industryForApi, serverData }) => {
    const { Data } = serverData;
    const distributionAvg = Data[industryForApi]?.DistributionAvg;
    const buckets = Data[industryForApi]?.Buckets;
    const leftBar = parseFloat(Object.keys(buckets)[0]);

    const leftWidgetTextReplacements = {
        xAxisRangeChange: getPercentageSignFilter(leftBar),
    };

    const bannerLessOrMoreText =
        distributionAvg > buckets[leftBar]
            ? i18n("market.analysis.industry.overview.market.distribution.banner.text.less")
            : i18n("market.analysis.industry.overview.market.distribution.banner.more");
    const bannerTextReplacement = { moreOrLess: bannerLessOrMoreText };
    const bannerText = i18n("market.analysis.industry.overview.market.distribution.banner.text", bannerTextReplacement);

    const chartConfig = getMarketDistributionBarChartConfig({ type: "column" });

    const chartData = useMemo(() => {
        if (!serverData) {
            return [];
        }
        const bars = Object.values(serverData.Data[industryForApi].Buckets).map((val) => {
            const percentageValue = val * 100;
            return { y: percentageValue, color: `${colorsPalettes.sky[300]}` };
        });
        const maxBar = orderBy(bars, "y", ["desc"])[0];
        maxBar.color = `${colorsPalettes.blue[400]}`;

        return [{ data: bars }];
    }, [industryForApi, serverData]);

    const LeftComponentLabel = (
        <I18n
            dataObj={leftWidgetTextReplacements}
            dangerouslySetInnerHTML={true}
        >
            {"market.analysis.industry.overview.market.distribution.left.widget.text"}
        </I18n>
    );
    return (
        <BodyContainer>
            <LeftContainer>
                <LeftMetricContainer>
                    <div data-automation="market-distribution-metric-data">
                        <MetricData
                            metricNameProps={{ displayName: LeftComponentLabel }}
                            valueProps={{ displayValue: percentageSignFilter()(buckets[leftBar]) }}
                        />
                    </div>
                </LeftMetricContainer>
            </LeftContainer>
            <RightContainer>
                <Banner>
                    <LightBulbStyled
                        iconName="light-bulb"
                        size="xs"
                    />
                    <BannerText>{bannerText}</BannerText>
                </Banner>
                <Chart
                    type={"column"}
                    data={chartData}
                    config={chartConfig}
                />
            </RightContainer>
        </BodyContainer>
    );
};
